import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import API_BASE_URL from "../config";
import "./Sidebar.css";

// Ordered the way a job moves through the studio: an enquiry arrives, becomes
// a lead, gets a quote from a package, turns into a client with a date in the
// calendar, and leaves a trail you can report on.
const CRM_LINKS = [
  { to: "/crm", icon: "📊", label: "Overview" },
  { to: "/crm/leads", icon: "🎯", label: "Leads" },
  { to: "/crm/quotes", icon: "📄", label: "Quotes" },
  { to: "/crm/packages", icon: "📦", label: "Packages" },
  { to: "/crm/clients", icon: "👥", label: "Clients" },
  { to: "/crm/calendar", icon: "📅", label: "Calendar" },
  { to: "/crm/followups", icon: "✅", label: "Follow-ups" },
  { to: "/crm/templates", icon: "💬", label: "Templates" },
  { to: "/crm/campaigns", icon: "📣", label: "Campaigns" },
  { to: "/crm/reports", icon: "📈", label: "Reports" },
  { to: "/crm/enquiry-form", icon: "🔗", label: "Enquiry Form" },
];

// CRM is Pro-tier. The sidebar hides the group for other plans; the API
// enforces the same rule, so this is presentation, not security. Cached in
// sessionStorage because the sidebar remounts on every page.
const CRM_PLANS = ["pro", "pro_plus", "pro_max_ultra"];

function useCrmAllowed() {
  // Keyed by user: an admin impersonating a studio, or a second login in the
  // same tab, must not inherit the previous account's answer.
  const cacheKey = `crmAllowed:${localStorage.getItem("userId") || "anon"}`;
  // Super admins are entitled regardless of plan, and we know that from
  // localStorage without asking the server. Deciding it synchronously matters:
  // the answer used to live entirely inside the fetch callback, so a slow,
  // failed or 401'd /api/user/:id left `allowed` false and the CRM group
  // silently vanished for someone who should see it.
  const isSuperAdmin = localStorage.getItem("userRole") === "super_admin";
  const [allowed, setAllowed] = useState(
    isSuperAdmin || sessionStorage.getItem(cacheKey) === "1"
  );

  useEffect(() => {
    const userId = localStorage.getItem("userId");
    const token = localStorage.getItem("token");
    if (!userId || !token) return;

    // The cached value is only a head start for the first paint — the check
    // always runs. It used to return early whenever anything was cached, so a
    // "no" recorded once (before an upgrade, or before the account's role was
    // known) stuck for the whole browser session and the CRM never appeared.
    fetch(`${API_BASE_URL}/api/user/${userId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((payload) => {
        // Non-ok (expired token, server hiccup): keep whatever we already
        // decided rather than revoking a menu the user is entitled to.
        if (!payload) return;
        // /api/user/:id answers { user: {...} }. Reading the envelope directly
        // left subscription_type undefined, so the CRM was hidden from every
        // paying studio and only showed for super_admins (the other branch).
        const user = payload.user || payload;
        // Cache the studio name while we're here: CRM message templates
        // substitute {{studio_name}}, and sessions that logged in before that
        // existed would otherwise render it blank.
        if (user.studio_name) localStorage.setItem("studioName", user.studio_name);
        // role comes from login-time localStorage — /api/user/:id doesn't return it.
        const ok = isSuperAdmin || CRM_PLANS.includes(user.subscription_type);
        sessionStorage.setItem(`crmAllowed:${userId}`, ok ? "1" : "0");
        setAllowed(ok);
      })
      .catch(() => {});
  }, [isSuperAdmin]);

  return allowed;
}

function Sidebar({ isOpen, onClose }) {
  const location = useLocation();
  const crmAllowed = useCrmAllowed();
  const onCrmPage = location.pathname.startsWith("/crm");
  // Stays open while you're anywhere in the CRM; collapsible by hand elsewhere.
  const [crmExpanded, setCrmExpanded] = useState(onCrmPage);
  return (
    <>
      {isOpen && <div className="sidebar-backdrop" onClick={onClose} />}
      <aside className={`sidebar ${isOpen ? "open" : "closed"}`}>
      <div className="sidebar-header">
        <h2 className="sidebar-logo">
          <img
            className="sidebar-logo__mark"
            src={process.env.PUBLIC_URL + "/photentra-mark.png"}
            alt=""
          />
          Photentra
        </h2>
      </div>

      <nav className="sidebar-nav">
        <ul>
          <li>
            <Link to="/dashboard" className="nav-link active">
              <span className="nav-icon">🏠</span>
              <span className="nav-text">Dashboard</span>
            </Link>
          </li>
          <li>
            <Link to="/events" className="nav-link">
              <span className="nav-icon">🎉</span>
              <span className="nav-text">Events</span>
            </Link>
          </li>
          {/* Desktop app only — the folder-watching engine lives in Electron. */}
          {window.photentra?.isDesktop && (
            <li>
              <Link to="/live-sync" className="nav-link">
                <span className="nav-icon">🔄</span>
                <span className="nav-text">Live Sync</span>
              </Link>
            </li>
          )}
          {crmAllowed && (
          <li>
            <button
              className={`nav-link nav-group-toggle ${onCrmPage ? "active" : ""}`}
              onClick={() => setCrmExpanded(!crmExpanded)}
              aria-expanded={crmExpanded || onCrmPage}
            >
              <span className="nav-icon">🤝</span>
              <span className="nav-text">CRM</span>
              <span className={`nav-caret ${crmExpanded || onCrmPage ? "open" : ""}`}>▾</span>
            </button>
            {(crmExpanded || onCrmPage) && (
              <ul className="nav-submenu">
                {CRM_LINKS.map((item) => (
                  <li key={item.to}>
                    <Link
                      to={item.to}
                      className={`nav-link nav-sublink ${location.pathname === item.to ? "active" : ""}`}
                    >
                      <span className="nav-icon">{item.icon}</span>
                      <span className="nav-text">{item.label}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </li>
          )}
          <li>
            <Link to="/portfolio" className="nav-link">
              <span className="nav-icon">📁</span>
              <span className="nav-text">Portfolio</span>
            </Link>
          </li>
          <li>
            <Link to="/wallet" className="nav-link">
              <span className="nav-icon">💰</span>
              <span className="nav-text">Wallet</span>
            </Link>
          </li>
          <li>
            <Link to="/qr-sharing" className="nav-link">
              <span className="nav-icon">📱</span>
              <span className="nav-text">QR Sharing</span>
            </Link>
          </li>
          <li>
            <Link to="/ai" className="nav-link">
              <span className="nav-icon">🤖</span>
              <span className="nav-text">Photentra AI</span>
            </Link>
          </li>
          <li>
            <Link to="/reminders" className="nav-link">
              <span className="nav-icon">⏰</span>
              <span className="nav-text">Reminders</span>
            </Link>
          </li>
          <li>
            <Link to="/ad-banners" className="nav-link">
              <span className="nav-icon">🖼️</span>
              <span className="nav-text">Ad Banners</span>
            </Link>
          </li>
          <li>
            <Link to="/my-website" className="nav-link">
              <span className="nav-icon">🌐</span>
              <span className="nav-text">My Website</span>
            </Link>
          </li>
          <li>
            <Link to="/photo-frames" className="nav-link">
              <span className="nav-icon">🖼️</span>
              <span className="nav-text">Photo Frames</span>
            </Link>
          </li>
          <li>
            <Link to="/photo-frame-orders" className="nav-link">
              <span className="nav-icon">📦</span>
              <span className="nav-text">Frame Orders</span>
            </Link>
          </li>
          <li>
            <Link to="/profile" className="nav-link">
              <span className="nav-icon">👤</span>
              <span className="nav-text">Profile</span>
            </Link>
          </li>
          {localStorage.getItem("userRole") !== "assistant" && (
            <li>
              <Link to="/team" className="nav-link">
                <span className="nav-icon">🧑‍🤝‍🧑</span>
                <span className="nav-text">Team</span>
              </Link>
            </li>
          )}
          {localStorage.getItem("userRole") === "super_admin" && (
            <li>
              <Link to="/admin" className="nav-link">
                <span className="nav-icon">🛡️</span>
                <span className="nav-text">Admin Panel</span>
              </Link>
            </li>
          )}
        </ul>
      </nav>

      <div className="sidebar-footer">
        <p>Photentra v1.0</p>
      </div>
    </aside>
    </>
  );
}

export default Sidebar;
